import React, { useState } from "react";
import { ref, set } from "firebase/database";
import { db } from "../services/firebase";
import { v4 as uuidv4 } from "uuid";
import { FaCheck, FaTimes, FaPlus, FaTrash, FaClock, FaFileUpload, FaCode } from "react-icons/fa";
import "./AdminAssessmentUpload.css";

function AdminAssessmentUpload({ user }) {
  const today = new Date().toISOString().split("T")[0];

  // ========== SHARED STATES ==========
  const [quizType, setQuizType] = useState("mcq"); // "mcq" | "codeSnippet" | "coding"
  const [quizId, setQuizId] = useState("");
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [date, setDate] = useState(today);
  
  // Exam Time Window Logic
  const [examStartTime, setExamStartTime] = useState(""); // e.g., "2024-01-15T10:00"
  const [examEndTime, setExamEndTime] = useState(""); // e.g., "2024-01-15T13:00"
  const [testDuration, setTestDuration] = useState("60"); // minutes
  
  const [allowReattempt, setAllowReattempt] = useState(false);
  const [showResultAfterSubmit, setShowResultAfterSubmit] = useState(true);
  const [delayedResultTime, setDelayedResultTime] = useState("0");

  // ========== MCQ STATES (WITH SECTIONS) ==========
  const [sections, setSections] = useState([]);
  const [currentSection, setCurrentSection] = useState({
    sectionTitle: "",
    sectionDuration: "",
    sectionMarks: "",
    questions: []
  });
  const [manualMCQ, setManualMCQ] = useState({
    question: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    explanation: "",
    marks: ""
  });

  // ========== CODE SNIPPET STATES ==========
  const [codeSnippetQuestions, setCodeSnippetQuestions] = useState([]);
  const [currentCodeSnippet, setCurrentCodeSnippet] = useState({
    title: "",
    description: "",
    codeSnippet: "",
    language: "javascript",
    questionType: "code_analysis",
    options: ["", "", "", ""],
    correctAnswer: "",
    explanation: "",
    marks: ""
  });

  // ========== CODING STATES ==========
  const [codingQuestions, setCodingQuestions] = useState([]);
  const [currentCodingQuestion, setCurrentCodingQuestion] = useState({
    title: "",
    description: "",
    marks: "",
    testCases: [{ input: "", expectedOutput: "", withLength: false }]
  });

  const [loading, setLoading] = useState(false);
  const [jsonPaste, setJsonPaste] = useState("");

  // ========== MCQ METHODS ==========
  const addSection = () => {
    if (!currentSection.sectionTitle || !currentSection.sectionDuration || currentSection.questions.length === 0) {
      alert("Section must have title, duration, and at least one question");
      return;
    }
    setSections([...sections, { ...currentSection }]);
    setCurrentSection({
      sectionTitle: "",
      sectionDuration: "",
      sectionMarks: "",
      questions: []
    });
  };

  const removeSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const addQuestionToSection = () => {
    if (!manualMCQ.question || manualMCQ.options.some((o) => !o) || !manualMCQ.correctAnswer || !manualMCQ.marks) {
      alert("Fill all MCQ fields including marks");
      return;
    }
    if (!manualMCQ.options.includes(manualMCQ.correctAnswer)) {
      alert("Correct answer must match one of the options");
      return;
    }
    const updated = {
      ...currentSection,
      questions: [...currentSection.questions, { ...manualMCQ }]
    };
    setCurrentSection(updated);
    setManualMCQ({
      question: "",
      options: ["", "", "", ""],
      correctAnswer: "",
      explanation: "",
      marks: ""
    });
  };

  const removeQuestionFromSection = (qIndex) => {
    setCurrentSection({
      ...currentSection,
      questions: currentSection.questions.filter((_, i) => i !== qIndex)
    });
  };

  const handleMCQChange = (field, value) => {
    setManualMCQ({ ...manualMCQ, [field]: value });
  };

  const handleOptionChange = (i, value) => {
    const updated = [...manualMCQ.options];
    updated[i] = value;
    setManualMCQ({ ...manualMCQ, options: updated });
  };

  const handleMCQJSONUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        const extracted = Array.isArray(data) ? data : Object.values(data).flat();
        const valid = extracted.filter(
          (q) => q.question && q.options?.length === 4 && q.correctAnswer && q.marks
        );
        if (valid.length === 0) {
          alert("No valid questions found in JSON");
          return;
        }
        const updated = {
          ...currentSection,
          questions: [...currentSection.questions, ...valid]
        };
        setCurrentSection(updated);
        alert(`✅ Added ${valid.length} questions from JSON`);
      } catch (err) {
        alert("Invalid JSON format");
      }
    };
    reader.readAsText(file);
  };

  const handleMCQJSONPaste = () => {
    try {
      const data = JSON.parse(jsonPaste);
      const extracted = Array.isArray(data) ? data : Object.values(data).flat();
      const valid = extracted.filter(
        (q) => q.question && q.options?.length === 4 && q.correctAnswer && q.marks
      );
      if (valid.length === 0) {
        alert("No valid questions found in JSON");
        return;
      }
      const updated = {
        ...currentSection,
        questions: [...currentSection.questions, ...valid]
      };
      setCurrentSection(updated);
      setJsonPaste("");
      alert(`✅ Added ${valid.length} questions from JSON`);
    } catch (err) {
      alert("Invalid JSON format");
    }
  };

  // ========== CODE SNIPPET METHODS ==========
  const addCodeSnippetQuestion = () => {
    if (!currentCodeSnippet.title || !currentCodeSnippet.codeSnippet || 
        currentCodeSnippet.options.some((o) => !o) || !currentCodeSnippet.correctAnswer || !currentCodeSnippet.marks) {
      alert("Fill all code snippet fields");
      return;
    }
    if (!currentCodeSnippet.options.includes(currentCodeSnippet.correctAnswer)) {
      alert("Correct answer must match one of the options");
      return;
    }
    setCodeSnippetQuestions([...codeSnippetQuestions, { ...currentCodeSnippet }]);
    setCurrentCodeSnippet({
      title: "",
      description: "",
      codeSnippet: "",
      language: "javascript",
      questionType: "code_analysis",
      options: ["", "", "", ""],
      correctAnswer: "",
      explanation: "",
      marks: ""
    });
  };

  const removeCodeSnippetQuestion = (index) => {
    setCodeSnippetQuestions(codeSnippetQuestions.filter((_, i) => i !== index));
  };

  const handleCodeSnippetChange = (field, value) => {
    setCurrentCodeSnippet({ ...currentCodeSnippet, [field]: value });
  };

  const handleCodeSnippetOptionChange = (i, value) => {
    const updated = [...currentCodeSnippet.options];
    updated[i] = value;
    setCurrentCodeSnippet({ ...currentCodeSnippet, options: updated });
  };

  const handleCodeSnippetJSONUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        const extracted = Array.isArray(data) ? data : Object.values(data).flat();
        const valid = extracted.filter(
          (q) => q.title && q.codeSnippet && q.options?.length === 4 && q.correctAnswer && q.marks
        );
        if (valid.length === 0) {
          alert("No valid questions found in JSON");
          return;
        }
        setCodeSnippetQuestions([...codeSnippetQuestions, ...valid]);
        alert(`✅ Added ${valid.length} code snippet questions from JSON`);
      } catch (err) {
        alert("Invalid JSON format");
      }
    };
    reader.readAsText(file);
  };

  // ========== CODING METHODS ==========
  const addCodingQuestion = () => {
    if (!currentCodingQuestion.title || !currentCodingQuestion.description || 
        !currentCodingQuestion.marks || currentCodingQuestion.testCases.length === 0) {
      alert("Fill all coding question fields including at least one test case");
      return;
    }
    setCodingQuestions([...codingQuestions, { ...currentCodingQuestion }]);
    setCurrentCodingQuestion({
      title: "",
      description: "",
      marks: "",
      testCases: [{ input: "", expectedOutput: "", withLength: false }]
    });
  };

  const removeCodingQuestion = (index) => {
    setCodingQuestions(codingQuestions.filter((_, i) => i !== index));
  };

  const handleCodingChange = (field, value) => {
    setCurrentCodingQuestion({ ...currentCodingQuestion, [field]: value });
  };

  const updateTestCase = (ti, field, value) => {
    const updated = [...currentCodingQuestion.testCases];
    updated[ti][field] = value;
    setCurrentCodingQuestion({ ...currentCodingQuestion, testCases: updated });
  };

  const addTestCase = () => {
    setCurrentCodingQuestion({
      ...currentCodingQuestion,
      testCases: [...currentCodingQuestion.testCases, { input: "", expectedOutput: "", withLength: false }]
    });
  };

  const removeTestCase = (ti) => {
    if (currentCodingQuestion.testCases.length === 1) {
      alert("At least one test case is required");
      return;
    }
    setCurrentCodingQuestion({
      ...currentCodingQuestion,
      testCases: currentCodingQuestion.testCases.filter((_, i) => i !== ti)
    });
  };

  // ========== UPLOAD HANDLERS ==========
  const validateTimeWindow = () => {
    if (!examStartTime || !examEndTime) {
      alert("Please set both exam start time and end time");
      return false;
    }
    const start = new Date(examStartTime);
    const end = new Date(examEndTime);
    if (start >= end) {
      alert("Exam end time must be after start time");
      return false;
    }
    if (!testDuration || parseInt(testDuration) <= 0) {
      alert("Test duration must be a positive number");
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    // Check if user is available first
    if (!user) {
      alert("❌ User not authenticated. Please login first.");
      return;
    }

    const id = quizId || uuidv4();
    if (!title || !topic) {
      alert("Fill title & topic");
      return;
    }

    if (!validateTimeWindow()) return;

    setLoading(true);

    try {
      if (quizType === "mcq") {
        if (sections.length === 0) {
          alert("Add at least one section with questions");
          setLoading(false);
          return;
        }

        // Calculate total duration and marks
        const totalDuration = sections.reduce((sum, s) => sum + parseInt(s.sectionDuration || 0), 0);
        const totalMarks = sections.reduce((sum, s) => {
          const sectionMarks = parseInt(s.sectionMarks || 0);
          const questionMarks = s.questions.reduce((qSum, q) => qSum + parseInt(q.marks || 0), 0);
          return sum + (sectionMarks || questionMarks);
        }, 0);

        const payload = {
          quizId: id,
          quizType: "mcq",
          topic,
          title,
          createdBy: user?.uid || user?.email || "admin",
          date,
          examStartTime,
          examEndTime,
          testDuration: parseInt(testDuration),
          totalDuration,
          totalMarks,
          allowReattempt,
          showResultAfterSubmit,
          delayedResultTime: showResultAfterSubmit ? null : delayedResultTime,
          sections: {}
        };

        sections.forEach((section, si) => {
          payload.sections[`section${si + 1}`] = {
            sectionTitle: section.sectionTitle,
            sectionDuration: parseInt(section.sectionDuration),
            sectionMarks: parseInt(section.sectionMarks || 0),
            questions: section.questions.map((q, qi) => ({
              question: q.question,
              options: q.options,
              correctAnswer: q.correctAnswer,
              explanation: q.explanation || "",
              marks: parseInt(q.marks)
            }))
          };
        });

        await set(ref(db, `quizzes/${id}`), payload);
        alert(`✅ MCQ Quiz uploaded successfully!\n\nQuiz ID: ${id}\n\nCopy this ID to link with student credentials when generating IDs.`);
        setQuizId(id); // Keep quizId visible for teacher to copy

      } else if (quizType === "codeSnippet") {
        if (codeSnippetQuestions.length === 0) {
          alert("Add at least one code snippet question");
          setLoading(false);
          return;
        }

        const totalMarks = codeSnippetQuestions.reduce((sum, q) => sum + parseInt(q.marks || 0), 0);

        const payload = {
          quizId: id,
          quizType: "codeSnippet",
          topic,
          title,
          createdBy: user?.uid || user?.email || "admin",
          date,
          examStartTime,
          examEndTime,
          testDuration: parseInt(testDuration),
          totalMarks,
          allowReattempt,
          showResultAfterSubmit,
          delayedResultTime: showResultAfterSubmit ? null : delayedResultTime,
          questions: {}
        };

        codeSnippetQuestions.forEach((q, qi) => {
          payload.questions[`q${qi + 1}`] = {
            title: q.title,
            description: q.description,
            codeSnippet: q.codeSnippet,
            language: q.language,
            questionType: q.questionType,
            options: q.options,
            correctAnswer: q.correctAnswer,
            explanation: q.explanation || "",
            marks: parseInt(q.marks)
          };
        });

        await set(ref(db, `codeSnippetQuestions/${id}`), payload);
        alert(`✅ Code Snippet Quiz uploaded successfully!\n\nQuiz ID: ${id}\n\nCopy this ID to link with student credentials when generating IDs.`);
        setQuizId(id); // Keep quizId visible for teacher to copy

      } else if (quizType === "coding") {
        if (codingQuestions.length === 0) {
          alert("Add at least one coding question");
          setLoading(false);
          return;
        }

        const totalMarks = codingQuestions.reduce((sum, q) => sum + parseInt(q.marks || 0), 0);

        const payload = {
          quizId: id,
          quizType: "coding",
          topic,
          title,
          createdBy: user?.uid || user?.email || "admin",
          date,
          examStartTime,
          examEndTime,
          testDuration: parseInt(testDuration),
          totalMarks,
          allowReattempt,
          showResultAfterSubmit,
          delayedResultTime: showResultAfterSubmit ? null : delayedResultTime,
          questions: {}
        };

        codingQuestions.forEach((q, qi) => {
          const qCopy = { ...q, testCases: [] };
          q.testCases.forEach((t) => {
            let input = t.input.trim();
            const values = input.split(/\s+/);
            const isNums = values.every((v) => /^-?\d+$/.test(v));
            if (t.withLength && isNums && values.length > 1) {
              input = `${values.length}\n${values.join(" ")}`;
            }
            qCopy.testCases.push({ input, expectedOutput: t.expectedOutput.trim() });
          });
          payload.questions[`q${qi + 1}`] = {
            title: qCopy.title,
            description: qCopy.description,
            marks: parseInt(qCopy.marks),
            testCases: qCopy.testCases
          };
        });

        await set(ref(db, `codingQuestions/${id}`), payload);
        alert(`✅ Coding Quiz uploaded successfully!\n\nQuiz ID: ${id}\n\nCopy this ID to link with student credentials when generating IDs.`);
        setQuizId(id); // Keep quizId visible for teacher to copy
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("❌ Upload failed: " + error.message);
    }

    setLoading(false);
  };

  const resetForm = () => {
    // Don't reset quizId - keep it so teacher can copy it
    // setQuizId("");
    setTitle("");
    setTopic("");
    setSections([]);
    setCurrentSection({
      sectionTitle: "",
      sectionDuration: "",
      sectionMarks: "",
      questions: []
    });
    setCodeSnippetQuestions([]);
    setCodingQuestions([]);
    setExamStartTime("");
    setExamEndTime("");
    setTestDuration("60");
  };

  const copyQuizId = () => {
    if (quizId) {
      navigator.clipboard.writeText(quizId);
      alert(`✅ Quiz ID copied: ${quizId}`);
    }
  };

  // ========== CALCULATE TOTALS ==========
  const getTotalMarks = () => {
    if (quizType === "mcq") {
      return sections.reduce((sum, s) => {
        const sectionMarks = parseInt(s.sectionMarks || 0);
        const questionMarks = s.questions.reduce((qSum, q) => qSum + parseInt(q.marks || 0), 0);
        return sum + (sectionMarks || questionMarks);
      }, 0);
    } else if (quizType === "codeSnippet") {
      return codeSnippetQuestions.reduce((sum, q) => sum + parseInt(q.marks || 0), 0);
    } else if (quizType === "coding") {
      return codingQuestions.reduce((sum, q) => sum + parseInt(q.marks || 0), 0);
    }
    return 0;
  };

  const getTotalQuestions = () => {
    if (quizType === "mcq") {
      return sections.reduce((sum, s) => sum + s.questions.length, 0);
    } else if (quizType === "codeSnippet") {
      return codeSnippetQuestions.length;
    } else if (quizType === "coding") {
      return codingQuestions.length;
    }
    return 0;
  };

  // ========== RENDER ==========
  return (
    <div className="admin-assessment-wrapper">
      <div className="assessment-header">
        <h2>📚 BodhAI Admin Assessment Upload</h2>
        <p className="subtitle">Unified dashboard for MCQ, Code Snippet & Coding quizzes</p>
      </div>

      {/* Quiz Type Toggle */}
      <div className="quiz-type-tabs">
        <button
          className={`tab-btn ${quizType === "mcq" ? "active" : ""}`}
          onClick={() => setQuizType("mcq")}
        >
          📝 MCQ (with Sections)
        </button>
        <button
          className={`tab-btn ${quizType === "codeSnippet" ? "active" : ""}`}
          onClick={() => setQuizType("codeSnippet")}
        >
          💻 Code Snippet MCQ
        </button>
        <button
          className={`tab-btn ${quizType === "coding" ? "active" : ""}`}
          onClick={() => setQuizType("coding")}
        >
          🔧 Coding / DSA
        </button>
      </div>

      <div className="assessment-panel">
        {/* Shared Inputs */}
        <div className="form-section">
          <h3>📋 Basic Information</h3>
          <div className="form-grid">
            <div>
              <label>Quiz ID (Auto-generated if empty)</label>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <input
                  type="text"
                  placeholder="Leave empty for auto UUID"
                  value={quizId}
                  onChange={(e) => setQuizId(e.target.value)}
                  style={{ flex: 1 }}
                />
                {quizId && (
                  <button 
                    type="button"
                    onClick={copyQuizId}
                    className="btn-secondary"
                    style={{ padding: "0.5rem 1rem", whiteSpace: "nowrap" }}
                  >
                    📋 Copy ID
                  </button>
                )}
              </div>
              {quizId && (
                <small style={{ color: "#666", marginTop: "0.25rem", display: "block" }}>
                  💡 Use this ID when generating student credentials to link them to this test
                </small>
              )}
            </div>
            <div>
              <label>Quiz Title *</label>
              <input
                type="text"
                placeholder="Enter quiz title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            <div>
              <label>Topic *</label>
              <input
                type="text"
                placeholder="Enter topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                required
              />
            </div>
            <div>
              <label>Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Exam Time Window */}
        <div className="form-section time-window-section">
          <h3>⏱️ Exam Time Window (IMPORTANT)</h3>
          <div className="time-window-info">
            <p>
              <strong>How it works:</strong> Candidates can join ANY TIME between start and end time.
              Timer = MIN(testDuration, examEndTime - currentJoinTime). Test auto-submits at examEndTime.
            </p>
          </div>
          <div className="form-grid">
            <div>
              <label>Exam Start Time *</label>
              <input
                type="datetime-local"
                value={examStartTime}
                onChange={(e) => setExamStartTime(e.target.value)}
                required
              />
            </div>
            <div>
              <label>Exam End Time *</label>
              <input
                type="datetime-local"
                value={examEndTime}
                onChange={(e) => setExamEndTime(e.target.value)}
                required
              />
            </div>
            <div>
              <label>Test Duration (minutes) *</label>
              <input
                type="number"
                placeholder="e.g., 60"
                value={testDuration}
                onChange={(e) => setTestDuration(e.target.value)}
                min="1"
                required
              />
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="form-section">
          <h3>⚙️ Settings</h3>
          <div className="settings-grid">
            <label>
              <input
                type="checkbox"
                checked={allowReattempt}
                onChange={(e) => setAllowReattempt(e.target.checked)}
              />
              Allow Reattempt
            </label>
            <label>
              <input
                type="checkbox"
                checked={showResultAfterSubmit}
                onChange={(e) => setShowResultAfterSubmit(e.target.checked)}
              />
              Show Result After Submit
            </label>
            {!showResultAfterSubmit && (
              <div>
                <label>Delay Result By (minutes)</label>
                <input
                  type="number"
                  value={delayedResultTime}
                  onChange={(e) => setDelayedResultTime(e.target.value)}
                  min="0"
                />
              </div>
            )}
          </div>
        </div>

        <hr className="section-divider" />

        {/* MCQ Section */}
        {quizType === "mcq" && (
          <div className="quiz-type-content">
            <h3>📝 MCQ Quiz with Sections</h3>
            
            {/* Current Section Builder */}
            <div className="section-builder">
              <h4>Current Section</h4>
              <div className="form-grid">
                <div>
                  <label>Section Title *</label>
                  <input
                    type="text"
                    placeholder="e.g., Section A"
                    value={currentSection.sectionTitle}
                    onChange={(e) => setCurrentSection({ ...currentSection, sectionTitle: e.target.value })}
                  />
                </div>
                <div>
                  <label>Section Duration (minutes) *</label>
                  <input
                    type="number"
                    placeholder="e.g., 30"
                    value={currentSection.sectionDuration}
                    onChange={(e) => setCurrentSection({ ...currentSection, sectionDuration: e.target.value })}
                    min="1"
                  />
                </div>
                <div>
                  <label>Section Marks (optional, auto-calculated from questions)</label>
                  <input
                    type="number"
                    placeholder="Auto-calculated"
                    value={currentSection.sectionMarks}
                    onChange={(e) => setCurrentSection({ ...currentSection, sectionMarks: e.target.value })}
                    min="0"
                  />
                </div>
              </div>

              {/* JSON Upload for Current Section */}
              <div className="json-upload-section">
                <h5>📤 Upload Questions via JSON</h5>
                <div className="json-methods">
                  <div>
                    <label>Upload JSON File</label>
                    <input type="file" accept=".json" onChange={handleMCQJSONUpload} />
                  </div>
                  <div>
                    <label>Paste JSON</label>
                    <textarea
                      placeholder='[{"question": "...", "options": ["A", "B", "C", "D"], "correctAnswer": "A", "marks": "5", "explanation": "..."}]'
                      value={jsonPaste}
                      onChange={(e) => setJsonPaste(e.target.value)}
                      rows="4"
                    />
                    <button onClick={handleMCQJSONPaste} className="btn-secondary">Add from JSON</button>
                  </div>
                </div>
              </div>

              {/* Manual Question Input */}
              <div className="question-input">
                <h5>➕ Add Question Manually</h5>
                <input
                  type="text"
                  placeholder="Question *"
                  value={manualMCQ.question}
                  onChange={(e) => handleMCQChange("question", e.target.value)}
                />
                <div className="options-grid">
                  {manualMCQ.options.map((opt, i) => (
                    <input
                      key={i}
                      type="text"
                      placeholder={`Option ${String.fromCharCode(65 + i)} *`}
                      value={opt}
                      onChange={(e) => handleOptionChange(i, e.target.value)}
                    />
                  ))}
                </div>
                <div className="form-grid">
                  <input
                    type="text"
                    placeholder="Correct Answer (must match one option) *"
                    value={manualMCQ.correctAnswer}
                    onChange={(e) => handleMCQChange("correctAnswer", e.target.value)}
                  />
                  <input
                    type="number"
                    placeholder="Marks *"
                    value={manualMCQ.marks}
                    onChange={(e) => handleMCQChange("marks", e.target.value)}
                    min="1"
                  />
                </div>
                <textarea
                  placeholder="Explanation (optional)"
                  value={manualMCQ.explanation}
                  onChange={(e) => handleMCQChange("explanation", e.target.value)}
                  rows="2"
                />
                <button onClick={addQuestionToSection} className="btn-primary">
                  ➕ Add Question to Section
                </button>
              </div>

              {/* Current Section Questions Preview */}
              {currentSection.questions.length > 0 && (
                <div className="preview-section">
                  <h5>Current Section Questions ({currentSection.questions.length})</h5>
                  <div className="questions-preview">
                    {currentSection.questions.map((q, qi) => (
                      <div key={qi} className="preview-item">
                        <div className="preview-header">
                          <strong>Q{qi + 1}: {q.question}</strong>
                          <button onClick={() => removeQuestionFromSection(qi)} className="btn-remove">
                            <FaTrash />
                          </button>
                        </div>
                        <ul>
                          {q.options.map((o, idx) => (
                            <li key={idx}>
                              {o} {o === q.correctAnswer && <FaCheck color="green" />}
                            </li>
                          ))}
                        </ul>
                        <p><strong>Marks:</strong> {q.marks}</p>
                        {q.explanation && <p><em>{q.explanation}</em></p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={addSection} className="btn-primary" disabled={currentSection.questions.length === 0}>
                ✅ Add Section ({currentSection.questions.length} questions)
              </button>
            </div>

            {/* Added Sections */}
            {sections.length > 0 && (
              <div className="sections-list">
                <h4>Added Sections ({sections.length})</h4>
                {sections.map((section, si) => (
                  <div key={si} className="section-item">
                    <div className="section-header">
                      <strong>
                        {section.sectionTitle} - {section.sectionDuration} min - {section.questions.length} questions
                      </strong>
                      <button onClick={() => removeSection(si)} className="btn-remove">
                        <FaTrash /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Code Snippet Section */}
        {quizType === "codeSnippet" && (
          <div className="quiz-type-content">
            <h3>💻 Code Snippet MCQ Quiz</h3>

            {/* JSON Upload */}
            <div className="json-upload-section">
              <h4>📤 Upload Questions via JSON</h4>
              <input type="file" accept=".json" onChange={handleCodeSnippetJSONUpload} />
            </div>

            {/* Manual Question Input */}
            <div className="question-input">
              <h4>➕ Add Code Snippet Question</h4>
              <div className="form-grid">
                <input
                  type="text"
                  placeholder="Question Title *"
                  value={currentCodeSnippet.title}
                  onChange={(e) => handleCodeSnippetChange("title", e.target.value)}
                />
                <select
                  value={currentCodeSnippet.language}
                  onChange={(e) => handleCodeSnippetChange("language", e.target.value)}
                >
                  <option value="javascript">JavaScript</option>
                  <option value="python">Python</option>
                  <option value="java">Java</option>
                  <option value="cpp">C++</option>
                  <option value="c">C</option>
                </select>
                <select
                  value={currentCodeSnippet.questionType}
                  onChange={(e) => handleCodeSnippetChange("questionType", e.target.value)}
                >
                  <option value="code_analysis">Code Analysis</option>
                  <option value="debugging">Debugging</option>
                  <option value="output_prediction">Output Prediction</option>
                  <option value="pseudocode">Pseudocode</option>
                  <option value="algorithm_understanding">Algorithm Understanding</option>
                </select>
              </div>
              <textarea
                placeholder="Description (optional)"
                value={currentCodeSnippet.description}
                onChange={(e) => handleCodeSnippetChange("description", e.target.value)}
                rows="2"
              />
              <label>Code Snippet *</label>
              <textarea
                className="code-textarea"
                placeholder="Paste code here..."
                value={currentCodeSnippet.codeSnippet}
                onChange={(e) => handleCodeSnippetChange("codeSnippet", e.target.value)}
                rows="8"
              />
              <div className="options-grid">
                {currentCodeSnippet.options.map((opt, i) => (
                  <input
                    key={i}
                    type="text"
                    placeholder={`Option ${String.fromCharCode(65 + i)} *`}
                    value={opt}
                    onChange={(e) => handleCodeSnippetOptionChange(i, e.target.value)}
                  />
                ))}
              </div>
              <div className="form-grid">
                <input
                  type="text"
                  placeholder="Correct Answer *"
                  value={currentCodeSnippet.correctAnswer}
                  onChange={(e) => handleCodeSnippetChange("correctAnswer", e.target.value)}
                />
                <input
                  type="number"
                  placeholder="Marks *"
                  value={currentCodeSnippet.marks}
                  onChange={(e) => handleCodeSnippetChange("marks", e.target.value)}
                  min="1"
                />
              </div>
              <textarea
                placeholder="Explanation (optional)"
                value={currentCodeSnippet.explanation}
                onChange={(e) => handleCodeSnippetChange("explanation", e.target.value)}
                rows="2"
              />
              <button onClick={addCodeSnippetQuestion} className="btn-primary">
                ➕ Add Code Snippet Question
              </button>
            </div>

            {/* Preview */}
            {codeSnippetQuestions.length > 0 && (
              <div className="preview-section">
                <h4>Preview Questions ({codeSnippetQuestions.length})</h4>
                <div className="questions-preview">
                  {codeSnippetQuestions.map((q, qi) => (
                    <div key={qi} className="preview-item">
                      <div className="preview-header">
                        <strong>{q.title} [{q.language}] - {q.questionType}</strong>
                        <button onClick={() => removeCodeSnippetQuestion(qi)} className="btn-remove">
                          <FaTrash />
                        </button>
                      </div>
                      <div className="code-preview">
                        <pre>{q.codeSnippet}</pre>
                      </div>
                      <p><strong>Marks:</strong> {q.marks}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Coding Section */}
        {quizType === "coding" && (
          <div className="quiz-type-content">
            <h3>🔧 Coding / DSA Quiz</h3>

            <div className="question-input">
              <h4>➕ Add Coding Question</h4>
              <input
                type="text"
                placeholder="Question Title *"
                value={currentCodingQuestion.title}
                onChange={(e) => handleCodingChange("title", e.target.value)}
              />
              <textarea
                placeholder="Description *"
                value={currentCodingQuestion.description}
                onChange={(e) => handleCodingChange("description", e.target.value)}
                rows="4"
              />
              <input
                type="number"
                placeholder="Marks *"
                value={currentCodingQuestion.marks}
                onChange={(e) => handleCodingChange("marks", e.target.value)}
                min="1"
              />

              <h5>Test Cases</h5>
              {currentCodingQuestion.testCases.map((tc, ti) => (
                <div key={ti} className="test-case-block">
                  <div className="test-case-header">
                    <strong>Test Case {ti + 1}</strong>
                    {currentCodingQuestion.testCases.length > 1 && (
                      <button onClick={() => removeTestCase(ti)} className="btn-remove">
                        <FaTrash />
                      </button>
                    )}
                  </div>
                  <div className="form-grid">
                    <div>
                      <label>Input *</label>
                      <textarea
                        placeholder="Input values"
                        value={tc.input}
                        onChange={(e) => updateTestCase(ti, "input", e.target.value)}
                        rows="3"
                      />
                    </div>
                    <div>
                      <label>Expected Output *</label>
                      <textarea
                        placeholder="Expected output"
                        value={tc.expectedOutput}
                        onChange={(e) => updateTestCase(ti, "expectedOutput", e.target.value)}
                        rows="3"
                      />
                    </div>
                  </div>
                  <label>
                    <input
                      type="checkbox"
                      checked={tc.withLength}
                      onChange={(e) => updateTestCase(ti, "withLength", e.target.checked)}
                    />
                    Prepend length (for array inputs)
                  </label>
                </div>
              ))}
              <button onClick={addTestCase} className="btn-secondary">
                ➕ Add Test Case
              </button>
              <button onClick={addCodingQuestion} className="btn-primary">
                ➕ Add Coding Question
              </button>
            </div>

            {/* Preview */}
            {codingQuestions.length > 0 && (
              <div className="preview-section">
                <h4>Preview Questions ({codingQuestions.length})</h4>
                <div className="questions-preview">
                  {codingQuestions.map((q, qi) => (
                    <div key={qi} className="preview-item">
                      <div className="preview-header">
                        <strong>{q.title} - {q.marks} marks</strong>
                        <button onClick={() => removeCodingQuestion(qi)} className="btn-remove">
                          <FaTrash />
                        </button>
                      </div>
                      <p>{q.description}</p>
                      <p><strong>Test Cases:</strong> {q.testCases.length}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Summary & Upload */}
        <div className="upload-summary">
          <div className="summary-stats">
            <div className="stat-item">
              <strong>Total Questions:</strong> {getTotalQuestions()}
            </div>
            <div className="stat-item">
              <strong>Total Marks:</strong> {getTotalMarks()}
            </div>
            {quizType === "mcq" && (
              <div className="stat-item">
                <strong>Total Duration:</strong>{" "}
                {sections.reduce((sum, s) => sum + parseInt(s.sectionDuration || 0), 0)} minutes
              </div>
            )}
          </div>
          <button
            onClick={handleUpload}
            disabled={loading || getTotalQuestions() === 0}
            className="btn-upload"
          >
            {loading ? "⏳ Uploading..." : "🚀 Upload Quiz"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminAssessmentUpload;
